/// <reference path="../type_definitions/ga.d.ts"/>

import * as ko from 'knockout';
import page from 'page';
import i18n from '../i18n';

import { session } from '../session';
import { BaseForm } from './base_form';
import { sha256 } from '../utils/hashing';
import * as tenantsApi from '../api/tenants';
import { TenantData } from '../api/base_tenants';

let template = require('raw-loader!../../templates/sign_up.html').default;

function sendToGoogleAnaltyicsIfEnabled(command: string, extra_parameters_json: any): void {
  if (typeof ga !== 'undefined') {
    // we don't have ga on dev builds
    ga(command, extra_parameters_json); // necessary for hitType pageview to work as expected
  }
}

async function sendRegistrationCompletedEvent(user_email: string) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'registration_completed',
    user_data: {
      sha256_email_address: await sha256(user_email),
    },
  });
}
class SignUpScreen extends BaseForm<{}> {
  featuresCurrentDiv = ko.observable(0);
  nextFeatureIntervalId: NodeJS.Timeout|null = null;
  savingProgressTrackerIntervalId: NodeJS.Timeout|null = null;
  savingProgress = ko.observable(0);
  nextFeatureIntervalTime = 40; // in seconds
  featuresMaxDivs = 5;
  validatedTenantData: TenantData|null = null;

  private updateProgressBar(nextInternalProgress: number, transitionTime: number) {
    /* Transitions the progress bar in 1% intervals */
    const currentProgress = Math.floor(nextInternalProgress);
    const progressDiff = currentProgress-this.savingProgress();
    if (progressDiff <= 0) {
      return;
    }
    const timeBetweenIncreases = Math.floor(transitionTime/progressDiff);
    const updateProgressInterval = setInterval(() => {
      const nextBarProgress = this.savingProgress()+1;
      if (nextBarProgress > nextInternalProgress){
        clearInterval(updateProgressInterval);
        return;
      }
      this.savingProgress(nextBarProgress);
    }, timeBetweenIncreases);
  }

  private beginProgressTracking(){
    /* We don't currently have an endpoint to check the progress, so the API call mocks it by returning */

    let timeBetweenUpdates = 1000;
    if(this.shouldCreateDemoData()){
      timeBetweenUpdates = 4900;
    }

    clearInterval(this.savingProgressTrackerIntervalId);
    let internalProgress = 1;
    this.savingProgress(internalProgress);
    internalProgress = this.getSavingProgressFromAPI(internalProgress);
    this.updateProgressBar(internalProgress, timeBetweenUpdates)

    this.savingProgressTrackerIntervalId = setInterval(() => {
      internalProgress = this.getSavingProgressFromAPI(internalProgress);
      this.updateProgressBar(internalProgress, timeBetweenUpdates)
      if (this.savingProgress() >= 98){
        // When over 98% we don't need to keep checking for progress.
        clearInterval(this.savingProgressTrackerIntervalId);
      }
    }, timeBetweenUpdates);
  }

  private getSavingProgressFromAPI(internalProgress: number){
    /*
    This is where an async API call would go to check for the task's actual progress
    if creating an account was an async task with a progress checking endpoint.
    
    Right now, it returns:
      - 0% to 80% in about one minute, and will take two more minutes to get from 80% to 98%
    */
    if (internalProgress < 74) {
      return internalProgress + 6.5;
    } else if (internalProgress < 97){
      return internalProgress + 0.75;
    } else {
      return 98;
    }
  }

  private startNextFeatureInterval(){
    clearInterval(this.nextFeatureIntervalId);
      this.nextFeatureIntervalId = setInterval(() => {
        this.featuresCurrentDiv((this.featuresCurrentDiv() + 1) % this.featuresMaxDivs);
    }, this.nextFeatureIntervalTime*1000);
  }

  goNextFeature(){
    this.featuresCurrentDiv((this.featuresCurrentDiv() + 1) % this.featuresMaxDivs);
    this.startNextFeatureInterval();
  }

  goPreviousFeature(){
    this.featuresCurrentDiv((this.featuresCurrentDiv() - 1 + this.featuresMaxDivs) % this.featuresMaxDivs);
    this.startNextFeatureInterval();
  }

  stepNumber = ko.observable(1);
  companyDetailsStep = ko.pureComputed(() => {
    return this.stepNumber() == 1;
  });
  loginStep = ko.pureComputed(() => {
    return this.stepNumber() == 2;
  });
  createAccountStep = ko.pureComputed(() => {
    return this.stepNumber() == 3;
  });
  loadingScreenStep = ko.pureComputed(() => {
    return this.stepNumber() == 4;
  });

  creatingInProgress = ko.observable(false);
  creatingDone = ko.observable(false);

  userEmail = ko.observable('');

  userName = ko.observable('').extend({
    required: true,
    maxLength: 100,
  });
  companyName = ko.observable('').extend({
    maxLength: 100,
    required: true,
  });
  role = ko.observable('').extend({
    maxLength: 100,
  });
  phoneNumber = ko.observable('').extend({
    maxLength: 100,
  });
  private companyDetailsFormGroup = ko.validation.group([
    this.userName,
    this.companyName,
    this.role,
    this.phoneNumber,
  ]);

  name = ko.observable('').extend({
    required: true,
    serverError: true,
  });
  accepted = ko.observable(false).extend({
    validation: {
      validator: (val: boolean) => {
        return val;
      },
      message: i18n.t(
        'You must accept the terms and conditions and the privacy policy to create an account'
      )(),
    },
  });

  shouldCreateDemoData = ko.observable(true);

  stepTitle = ko.pureComputed(() =>
    i18n.t('Step {{ stepNumber }} of 3', { stepNumber: this.stepNumber() })()
  );

  loadingScreenTitle = ko.pureComputed(() =>
    i18n.t('QuickTrials is setting up the {{ orgName }} organization.', { orgName: this.companyName() })()
  );


  private accountFormGroup = ko.validation.group([this.name, this.accepted]);

  supportEmail = SERVER_INFO.SUPPORT_EMAIL;
  termsAndConditionsURL = APP_CONFIG.TERMS_AND_CONDITIONS_URL;
  privacyPolicyURL = APP_CONFIG.PRIVACY_POLICY_URL;
  defaultAccountExpirationDays = SERVER_INFO.DEFAULT_ACCOUNT_EXPIRATION_DAYS;

  private planId: string;
  private subscription: KnockoutSubscription = null;

  constructor(params: { planId?: string }) {
    super({});

    this.planId = params.planId;

    this.subscription = session.loggedIn.subscribe(this.onLoginStateChange.bind(this));
    this.onLoginStateChange();

    this.loaded();
    this.startNextFeatureInterval();

    this.saving.subscribe((value) => {
      if (value !== true){
        return;
      }
      this.beginProgressTracking();
    })
  }

  dispose() {
    if (this.subscription) {
      this.subscription.dispose();
    }
  }

  close() {}

  saveCompanyDetails = () => {
    if (this.companyDetailsFormGroup().length > 0) {
      this.companyDetailsFormGroup.showAllMessages();
      return;
    }

    this.nextStep();
  };

  private onLoginStateChange() {
    if (this.companyDetailsStep()) {
      return;
    }

    this.nextStep();
  }

  private nextStep() {
    if (session.loggedIn()) {
      this.userEmail(session.getEmail());

      let name = this.companyName() || session.getName();
      if (name && !this.name()) {
        this.name(name);
      }
      // Not true but Googla Analytics does not care
      sendToGoogleAnaltyicsIfEnabled('send', {
        hitType: 'pageview',
        page: '/sign-up/step3',
      });
      this.stepNumber(3);
    } else {
      // Not true but Googla Analytics does not care
      sendToGoogleAnaltyicsIfEnabled('send', {
        hitType: 'pageview',
        page: '/sign-up/step2',
      });
      this.stepNumber(2);
    }
  }

  redirectToStart() {
    if (!this.validatedTenantData){
      return;
    }

    if (this.planId) {
      page.redirect(
        session.toTenantPath('/subscription/?plan_id=' + this.planId, this.validatedTenantData)
      );
    } else {
      page.redirect(session.toTenantPath('/', this.validatedTenantData));
    }
  }

  save = () => {
    if (this.validate(this.accountFormGroup)) {
      this.stepNumber(4);
      this.creatingInProgress(true);
      this.creatingDone(false);

      // Don't wait for the tenant creation request to complete. This is already counted as a conversion.
      sendRegistrationCompletedEvent(session.getEmail());

      let data = {
        name: this.name(),
        admin_email: session.getEmail(),
        admin_name: this.userName() || session.getName(),
        company_name: this.companyName(),
        role: this.role(),
        phone_number: this.phoneNumber(),
        should_create_demo_data: this.shouldCreateDemoData(),
      };

      this.executeSaveRequest(tenantsApi.save(data)).then((validation) => {
        this.onRemoteValidation({}, this, validation);

        if (validation.tenantData) {
          if (typeof ga !== 'undefined') {
            // we don't have ga on dev builds
            ga('send', { hitType: 'pageview', page: '/sign-up/completed' }); // Not true but Google Analytics does not care
          }
          this.validatedTenantData = validation.tenantData;
          this.creatingInProgress(false);
          this.creatingDone(true);
        }
      });
    }
  };
}

sendToGoogleAnaltyicsIfEnabled('set', { hostname: location.hostname }); // necessary for hitType pageview to work as expected

export let signUp = {
  name: 'sign-up',
  viewModel: SignUpScreen,
  template: template,
};

ko.components.register(signUp.name, signUp);
